import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { DEFAULT_SETTINGS, checkDraft, checkSettings, normalizeSettings } from '../utils/followups';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];

export const isMissingFollowups = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205', '42703', 'PGRST204'].includes(error?.code) || /followups?|followup_settings|approve_followup|no_followup/i.test(error?.message || '');

const explain = (error, fallback) => (error?.code === '42501' ? 'No tienes permiso sobre este negocio (solo el dueño o un administrador).' : fallback);

// Seguimiento automático: ajustes, mensajes por aprobar e historial. Las reglas y los límites viven en la base (20261018a_followups.sql).
export const useFollowups = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId) { setLoading(false); return; }
    setLoading(true);
    const business = String(activeBusinessId);
    const [s, f] = await Promise.all([
      supabase.from('followup_settings').select('*').eq('business_id', business).maybeSingle(),
      supabase.from('followups').select('id, kind, touch, status, customer_name, phone, context, draft, approved_by, error, sent_at, created_at').eq('business_id', business).order('created_at', { ascending: false }).limit(100)
    ]);
    const error = s.error || f.error;
    if (error) {
      const gone = isMissingFollowups(error);
      setMissing(gone);
      if (!gone) toast.error(explain(error, 'No se pudo leer el seguimiento.'));
      setItems([]);
    } else {
      setMissing(false);
      setSettings(normalizeSettings(s.data));
      setItems(f.data || []);
    }
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const saveSettings = async (values) => {
    if (guardDemo()) return false;
    const problem = checkSettings(values);
    if (problem) { toast.error(problem); return false; }
    const row = {
      business_id: String(activeBusinessId), enabled: Boolean(values.enabled), require_approval: Boolean(values.require_approval),
      rule_lead: Boolean(values.rule_lead), rule_quote: Boolean(values.rule_quote), rule_rebook: Boolean(values.rule_rebook),
      lead_after_hours: Number(values.lead_after_hours), quote_after_days: Number(values.quote_after_days), rebook_after_days: Number(values.rebook_after_days),
      max_touches: Number(values.max_touches), daily_cap: Number(values.daily_cap), updated_at: new Date().toISOString()
    };
    const { error } = await supabase.from('followup_settings').upsert([row], { onConflict: 'business_id' });
    if (error) { toast.error(explain(error, 'No se pudieron guardar los ajustes.')); return false; }
    setSettings(normalizeSettings(row));
    toast.success(row.enabled ? 'Seguimiento guardado' : 'Seguimiento apagado');
    return true;
  };

  const approve = async (id, text) => {
    if (guardDemo()) return false;
    const problem = checkDraft(text);
    if (problem) { toast.error(problem); return false; }
    const { data, error } = await supabase.rpc('approve_followup', { p_id: id, p_text: String(text).trim() });
    if (error) { toast.error(explain(error, 'No se pudo aprobar el mensaje.')); return false; }
    if (data === 'ya_cerrado') toast.error('Ese mensaje ya no está por aprobar.');
    else if (data === 'texto_invalido') toast.error('El mensaje no es válido.');
    else toast.success('Aprobado: saldrá en el próximo horario de envío (lunes a sábado, 8 a 20)');
    await load();
    return data === 'aprobado';
  };

  const discard = async (id) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('discard_followup', { p_id: id });
    if (error) { toast.error(explain(error, 'No se pudo descartar el mensaje.')); return false; }
    toast.success('Mensaje descartado');
    await load();
    return true;
  };

  return { settings, items, loading, missing, isReadOnlyDemo, reload: load, saveSettings, approve, discard };
};
